import { DateMetaData } from "burde";
import * as moment from "moment";
import { Moment } from "moment";
import { D3Selection } from "./draw-calendar";
import { showRedText } from "./dynamic-layout/helper";
import {
    colors,
    dayLetterElementIdentifiers,
} from "./dynamic-layout/constants";
import * as d3 from "d3";

const drawDynamicExtraPage = (
    monthDividerElement: D3Selection,
    startDate: Moment,
    metaData: DateMetaData[],
    culture: string | null,
    Header_WeekNumberPrefix: string,
) => {
    // The monday at the beginning of the first week that has days that are part of the month.
    let iterationDate = getIterationDate(startDate);
    const monthToDraw = getMonthToDraw(iterationDate);
    const yearToDraw = getYearToDraw(iterationDate);

    drawMonthName(monthDividerElement, monthToDraw);
    drawWeekNumbers(
        monthDividerElement,
        iterationDate,
        monthToDraw,
        Header_WeekNumberPrefix,
    );
    drawDayLetters(monthDividerElement, iterationDate);
    drawDayNumbers(
        monthDividerElement,
        iterationDate,
        monthToDraw,
        metaData,
        culture,
    );
    drawYearNumber(monthDividerElement, yearToDraw);
};

/* 
The month to draw should be whatever month the startDate is, unless the next month starts
within the same week.
*/
const getMonthToDraw = (startDate: Moment) => {
    const currentMonth = startDate.clone().format("MM");
    const monthAtEndOfWeek = startDate
        .clone()
        .endOf("isoWeek")
        .add(1, "day")
        .format("MM");

    if (currentMonth === monthAtEndOfWeek) {
        return startDate.format("MM");
    }

    return monthAtEndOfWeek;
};

/* 
The year to draw should be whatever month the startDate is, unless the next year starts
within the same week.
*/
const getYearToDraw = (startDate: Moment) => {
    const currentYear = startDate.clone().format("YYYY");
    const yearAtEndOfWeek = startDate
        .clone()
        .endOf("isoWeek")
        .add(1, "day")
        .format("YYYY");

    if (currentYear === yearAtEndOfWeek) {
        return startDate.format("YYYY");
    }

    return yearAtEndOfWeek;
};

/*
The iteration date should be the start of the week when the starting month begins.
*/
const getIterationDate = (startDate: Moment) => {
    const dateAtEndOfWeek = startDate.clone().endOf("isoWeek");
    let iterationDate = startDate.clone();

    iterationDate = moment(
        dateAtEndOfWeek.clone().startOf("month").startOf("isoWeek"),
    );

    return iterationDate;
};

const drawYearNumber = (
    monthDividerElement: D3Selection,
    yearToDraw: string,
) => {
    const yearNumberContainer = monthDividerElement.select(".yearNumber");
    const yearNumberElement = yearNumberContainer.selectAll("tspan");

    yearNumberElement.text(yearToDraw);
};

const drawMonthName = (
    monthDividerElement: D3Selection,
    monthToDraw: string,
) => {
    const monthNameContainer = monthDividerElement.select(".monthName");
    const monthNameElement = monthNameContainer.selectAll("tspan");

    const monthName = moment(monthToDraw, "MM").format("MMMM");
    const formattedMonthName =
        monthName.charAt(0).toUpperCase() + monthName.slice(1);

    monthNameElement.text(formattedMonthName);
};

const drawWeekNumbers = (
    monthDividerElement: D3Selection,
    iterationDate: Moment,
    monthToDraw: string,
    Header_WeekNumberPrefix: string,
) => {
    const weekNumberContainer = monthDividerElement.select(".weekNumbers");
    const weekElements = weekNumberContainer.selectAll("text");

    weekElements.each(function (d: unknown, i: number) {
        // @ts-ignore
        const className = this.getAttribute("class");
        const weekNumber = className.replace(/[^0-9]/g, "");
        const weekElementContainer = weekNumberContainer.select(
            `.weekNumber${weekNumber}`,
        );

        const weekElementSpan = weekElementContainer.select("tspan");

        const currentDate = moment(
            iterationDate
                .clone()
                .add(weekNumber - 1, "weeks")
                .format("YYYY-MM-DD"),
            "YYYY-MM-DD",
        );

        const week = currentDate.isoWeek();
        weekElementSpan.text(`${Header_WeekNumberPrefix}${week}`);
    });
};

const drawDayNumbers = (
    monthDividerElement: D3Selection,
    iterationDate: Moment,
    monthToDraw: string,
    metaData: DateMetaData[],
    culture: string | null,
) => {
    const dayNumberContainer = monthDividerElement.select(".DayData");
    const dayElements = dayNumberContainer.selectAll("text");

    dayElements.each(function (d: unknown, i: number) {
        // @ts-ignore
        const className = this.getAttribute("class");
        const dayNumber = className.replace(/[^0-9]/g, "");
        const dayElementContainer = dayNumberContainer.select(
            `.dayNumber${dayNumber}`,
        );

        const currentDate = moment(
            iterationDate
                .clone()
                .add(dayNumber - 1, "days")
                .format("YYYY-MM-DD"),
            "YYYY-MM-DD",
        );

        const iterationMonth = currentDate.clone().format("MM");
        const dayElement = dayElementContainer.select("tspan");

        if (iterationMonth !== monthToDraw) {
            dayElement.attr("fill", "#a1a1a1");
        } else {
            const dateMetaData = metaData.find(
                (d) => d.date === currentDate.format("YYYYMMDD"),
            );

            if (showRedText(currentDate, dateMetaData, culture)) {
                let accentColor = colors.redDay;
                dayElement.attr("fill", accentColor);
            } else {
                dayElement.attr("fill", "#555");
            }
        }

        dayElement.text(`${currentDate.clone().format("D")}`);
    });
};

const drawDayLetters = (
    monthDividerElement: D3Selection,
    startOfWeek: Moment,
) => {
    let iterationDate = startOfWeek.clone();
    const dayNumberContainer = monthDividerElement.select(".dayLetters");

    dayLetterElementIdentifiers.forEach((className) => {
        const dayElements = dayNumberContainer.selectAll(`.${className}`);

        dayElements.each(function (d: unknown, i: number) {
            const dayElement = d3.select(this);
            dayElement.text(
                iterationDate.format("dddd").charAt(0).toUpperCase(),
            );
        });

        iterationDate = moment(
            iterationDate.add(1, "day").format("YYYY-MM-DD"),
            "YYYY-MM-DD",
        );
    });
};

export default drawDynamicExtraPage;
